import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Slider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  states,
  USER_LOAN_PROGRAM,
  userLoanProgramMap,
} from '@/config/constants';
import { useCreateProductMutation } from '@/apis/extended/productApi';
import { useUpsertRateAlertMutation } from '@/apis/extended/rateAlertApi';
import { useUpdateUserMutation } from '@/apis/extended/userApi';
import { GlassCard } from '@/components/ui/GlassCard';
import {
  buildRateWatchProductPayload,
  defaultProgramForPropertyUse,
  loanProgramForRateAlert,
  userProfilePatchForRateWatch,
  type RateWatchPropertyUse,
  type RateWatchWizardForm,
} from '@/utils/rateWatchOnboarding';

const STEPS = ['Property use', 'Mortgage snapshot', 'Enable watch'];

const PRIMARY_PROGRAMS = [
  USER_LOAN_PROGRAM.CONVENTIONAL,
  USER_LOAN_PROGRAM.FHA,
  USER_LOAN_PROGRAM.VA,
];

const parseNumber = (value: string) => {
  if (value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const initialForm = (): RateWatchWizardForm => ({
  propertyUse: 'primary',
  currentRate: 6.75,
  street: '',
  city: '',
  state: '',
  zip: '',
  propertyValue: 0,
  balance: 250000,
  termYears: 30,
  monthlyPayment: 0,
  program: USER_LOAN_PROGRAM.CONVENTIONAL,
  spreadBps: 25,
});

export const RateWatchOnboardingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<RateWatchWizardForm>(initialForm);
  const [showOptional, setShowOptional] = useState(false);

  const [createProduct, { isLoading: creating }] = useCreateProductMutation();
  const [upsertAlert, { isLoading: savingAlert }] =
    useUpsertRateAlertMutation();
  const [updateUser, { isLoading: savingUser }] = useUpdateUserMutation();

  const isSubmitting = creating || savingAlert || savingUser;

  useEffect(() => {
    if (form.propertyUse === 'investment') {
      setForm((f) => ({
        ...f,
        program: USER_LOAN_PROGRAM.DSCR,
      }));
    }
  }, [form.propertyUse]);

  const effectiveThreshold = useMemo(
    () => form.currentRate - form.spreadBps / 100,
    [form.currentRate, form.spreadBps]
  );

  const stepValid = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) {
      return (
        form.currentRate > 0 &&
        form.street.trim() !== '' &&
        form.city.trim() !== '' &&
        form.state.trim() !== '' &&
        form.zip.trim() !== '' &&
        form.propertyValue > 0
      );
    }
    return form.currentRate > 0;
  }, [step, form]);

  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  const handlePropertyUse = (
    _: React.MouseEvent<HTMLElement>,
    value: RateWatchPropertyUse | null
  ) => {
    if (!value) return;
    setForm((f) => ({
      ...f,
      propertyUse: value,
      program: defaultProgramForPropertyUse(value),
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = buildRateWatchProductPayload(form);
      const product = await createProduct(payload).unwrap();
      const productId = product?._id;
      if (!productId) {
        throw new Error('Missing product id');
      }

      const loanProgram = loanProgramForRateAlert(
        form.propertyUse,
        form.program
      );
      await upsertAlert({
        propertyId: productId,
        threshold: effectiveThreshold,
        currentRatePct: form.currentRate,
        spreadBps: form.spreadBps,
        loanProgram,
        enabled: true,
      }).unwrap();

      await updateUser(
        userProfilePatchForRateWatch(form.propertyUse, form.program)
      ).unwrap();

      toast.success('Rate watch enabled');
      navigate(`/refinance/summary/${productId}`);
    } catch (err) {
      console.error('Rate watch onboarding failed', err);
      toast.error('Could not set up rate watch. Please try again.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ mb: 3 }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => navigate('/onboarding')}
          startIcon={<i className="fa-light fa-arrow-left" />}
          sx={{ mb: 2 }}
        >
          Back to goals
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Set up rate watch
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {form.propertyUse === 'investment'
            ? 'We’ll watch investor refinance rates against your current loan.'
            : 'We’ll compare market rates to your current mortgage on your home.'}
        </Typography>
      </Box>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <GlassCard variant="medium" sx={cardSx}>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {step === 0 && (
            <Stack spacing={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                What kind of property is this?
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={form.propertyUse}
                onChange={handlePropertyUse}
              >
                <ToggleButton value="primary">Primary residence</ToggleButton>
                <ToggleButton value="investment">
                  Investment property
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="body2" color="text.secondary">
                {form.propertyUse === 'primary'
                  ? 'We’ll use conventional, FHA, or VA rate programs for your watch.'
                  : 'We’ll use DSCR / investor rate programs for your watch.'}
              </Typography>
            </Stack>
          )}

          {step === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Current mortgage rate (%)"
                  type="number"
                  fullWidth
                  required
                  value={form.currentRate || ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      currentRate: parseNumber(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated property value"
                  type="number"
                  fullWidth
                  required
                  value={form.propertyValue || ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      propertyValue: parseNumber(e.target.value),
                    }))
                  }
                />
              </Grid>
              {form.propertyUse === 'primary' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Loan program"
                    fullWidth
                    value={form.program}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        program: e.target.value as USER_LOAN_PROGRAM,
                      }))
                    }
                  >
                    {PRIMARY_PROGRAMS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {userLoanProgramMap[p]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Street address"
                  fullWidth
                  required
                  value={form.street}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, street: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  fullWidth
                  required
                  value={form.city}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, city: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="State"
                  fullWidth
                  required
                  value={form.state}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, state: e.target.value }))
                  }
                >
                  {/* Entries are { label, value }; rendering the object itself
                      crashed the page when the menu opened. */}
                  {states.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Zip"
                  fullWidth
                  required
                  value={form.zip}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, zip: e.target.value }))
                  }
                />
              </Grid>

              {!showOptional ? (
                <Grid item xs={12}>
                  <Button size="small" onClick={() => setShowOptional(true)}>
                    Add balance & term for payoff comparisons (optional)
                  </Button>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Current balance"
                      type="number"
                      fullWidth
                      value={form.balance || ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          balance: parseNumber(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Remaining term (years)"
                      type="number"
                      fullWidth
                      value={form.termYears || ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          termYears: parseNumber(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Monthly P&I"
                      type="number"
                      fullWidth
                      value={form.monthlyPayment || ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          monthlyPayment: parseNumber(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {step === 2 && (
            <Stack spacing={3}>
              <Alert severity="info">
                You&apos;re watching rates — we&apos;ll email you when market
                rates drop {form.spreadBps} bps below your{' '}
                {form.currentRate.toFixed(2)}% mortgage (at or under{' '}
                {effectiveThreshold.toFixed(2)}%).
              </Alert>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Alert when rates are this far below your mortgage
                </Typography>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={form.spreadBps}
                  onChange={(_, v) =>
                    setForm((f) => ({ ...f, spreadBps: v as number }))
                  }
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v} bps`}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Program: {userLoanProgramMap[form.program] ?? form.program} ·{' '}
                {form.propertyUse === 'primary'
                  ? 'Primary residence'
                  : 'Investment property'}
              </Typography>
            </Stack>
          )}
        </Box>
      </GlassCard>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button
          disabled={step === 0 || isSubmitting}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!stepValid}
            onClick={() => setStep((s) => s + 1)}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!stepValid || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Setting up…' : 'Enable rate watch'}
          </Button>
        )}
      </Stack>
    </Container>
  );
};
